import { type ReactNode } from 'react'
import { Link } from 'react-router'
import { FenceAuthIcon, FenceAuthLogo } from '@/components/brand/FenceAuthLogo'
import { cn } from '@/lib/utils'

/**
 * Shells for the P1 auth family (Auth mockup 1a–1f): a light split layout with
 * the dark promo rail, a dark centered card (SSO-resolved, MFA), and a light
 * card page for hold/terminal states.
 */

export function AuthSplitShell({
  children,
  rail,
}: {
  children: ReactNode
  /** Right rail content; defaults to the marketing promo panel (1a). */
  rail?: ReactNode
}) {
  return (
    <div className="grid min-h-dvh bg-surface-raised lg:grid-cols-[1fr_440px]">
      <div className="flex flex-col justify-center px-6 py-10 sm:px-14 lg:px-20">
        <div className="mx-auto w-full max-w-md">
          <Link to="/" aria-label="FenceAuth home">
            <FenceAuthLogo className="mb-8 h-8" />
          </Link>
          {children}
        </div>
      </div>
      <div className="sidebar-texture relative hidden flex-col justify-end overflow-hidden bg-sidebar p-10 lg:flex">
        <div
          className="pointer-events-none absolute -top-10 -right-8 flex gap-4 opacity-50"
          aria-hidden
        >
          <span className="animate-float h-[300px] w-[22px] rounded-[11px] bg-gradient-to-b from-accent/50 to-transparent" />
          <span className="animate-float-slow h-[380px] w-[22px] rounded-[11px] bg-gradient-to-b from-white/15 to-transparent" />
          <span className="animate-float h-[250px] w-[22px] rounded-[11px] bg-gradient-to-b from-white/10 to-transparent [animation-delay:1.5s]" />
        </div>
        <div className="relative">{rail ?? <AuthPromoRail />}</div>
      </div>
    </div>
  )
}

function AuthPromoRail() {
  return (
    <div className="flex flex-col gap-6">
      <p className="max-w-[330px] text-xl leading-snug font-semibold tracking-tight text-white">
        One session layer for every product you ship.
      </p>
      <ul className="flex flex-col gap-3">
        {[
          'OIDC + SAML enterprise SSO',
          'SCIM 2.0 provisioning',
          'Entitlements, metering, dunning',
        ].map((item) => (
          <li key={item} className="flex items-center gap-2.5 text-[13px] text-sidebar-fg">
            <span className="size-1.5 rounded-full bg-accent" aria-hidden />
            {item}
          </li>
        ))}
      </ul>
      <pre className="rounded-[10px] border border-white/10 bg-white/[0.03] px-4 py-3.5 font-mono text-[11.5px] leading-relaxed text-[#8FA0B8]">
        <span className="text-accent">GET</span> /api/v1/auth/login?redirect=/dashboard
        {'\n'}
        <span className="text-content-secondary">302 →</span> keycloak → /auth/callback{'\n'}
        <span className="text-content-secondary">Set-Cookie:</span> fa_session; SameSite=Lax
      </pre>
    </div>
  )
}

/** Full dark page with a centered card (mockups 1b, 1e). */
export function DarkAuthShell({
  children,
  showWordmark = false,
}: {
  children: ReactNode
  showWordmark?: boolean
}) {
  return (
    <div className="sidebar-texture relative flex min-h-dvh items-center justify-center overflow-hidden bg-sidebar px-4 py-10">
      <div
        className="pointer-events-none absolute top-0 left-1/2 h-[220px] w-[520px] -translate-x-1/2 bg-[radial-gradient(ellipse_at_top,rgb(20_184_166/0.18),transparent_70%)]"
        aria-hidden
      />
      <div className="relative w-full max-w-[452px] rounded-[14px] border border-white/10 bg-[#131C31] p-8 shadow-[0_24px_60px_rgb(0_0_0/0.45)] sm:p-10">
        {showWordmark ? (
          <FenceAuthLogo onDark className="mb-7 h-7" />
        ) : (
          <FenceAuthIcon onDark className="mb-6 size-[34px]" />
        )}
        {children}
      </div>
      <p className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-4 text-[11.5px] whitespace-nowrap text-[#7C8CA6]">
        <span>SOC 2 Type II</span>
        <span className="size-[3px] rounded-full bg-[#3C4A62]" aria-hidden />
        <span>Status</span>
        <span className="size-[3px] rounded-full bg-[#3C4A62]" aria-hidden />
        <span>Docs</span>
      </p>
    </div>
  )
}

/** Light page with the logo and a centered card (hold + terminal states, 1d/1f). */
export function LightAuthShell({
  children,
  headerNote,
  maxWidth = 'max-w-xl',
}: {
  children: ReactNode
  headerNote?: ReactNode
  maxWidth?: string
}) {
  return (
    <div className="flex min-h-dvh flex-col bg-surface-page px-5 py-10 sm:px-12">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <Link to="/" aria-label="FenceAuth home">
          <FenceAuthLogo className="h-7" />
        </Link>
        {headerNote ? <span className="text-xs text-content-secondary">{headerNote}</span> : null}
      </div>
      <div className={cn('mx-auto w-full flex-1', maxWidth)}>{children}</div>
    </div>
  )
}
